/**
 * Unified Easing System
 * 
 * Provides a centralized set of easing functions used throughout the game for:
 * - Animation blending
 * - Camera transitions
 * - Impact effects (time ramps)
 * - Game time manipulation (slow-mo, speed-up)
 * 
 * All functions take normalized time t ∈ [0, 1] and return ∈ [0, 1]
 */

/**
 * An easing function maps normalized time [0, 1] to an eased value [0, 1]
 */
export type EasingFunction = (t: number) => number;

/**
 * Preset identifiers for built-in easing functions
 */
export type EasingPreset =
  // Standard easing curves
  | 'linear'
  | 'easeIn'         // Quadratic ease in
  | 'easeOut'        // Quadratic ease out
  | 'easeInOut'      // Quadratic ease in-out
  | 'easeInCubic'    // Cubic ease in
  | 'easeOutCubic'   // Cubic ease out
  | 'easeInOutCubic' // Cubic ease in-out
  // Special effect curves
  | 'punch'          // Sharp attack, quick decay
  | 'bounce'         // Multiple diminishing peaks
  | 'explosion'      // Quick peak then exponential fade
  | 'instant';       // Step function (no interpolation)

/**
 * Core easing function implementations
 */
export const Easing: Record<EasingPreset, EasingFunction> = {
  /**
   * No easing - constant rate of change
   */
  linear: (t: number): number => t,

  /**
   * Quadratic ease in - slow start, accelerating
   */
  easeIn: (t: number): number => t * t,

  /**
   * Quadratic ease out - fast start, decelerating
   */
  easeOut: (t: number): number => 1 - (1 - t) * (1 - t),

  /**
   * Quadratic ease in-out - slow at both ends
   */
  easeInOut: (t: number): number =>
    t < 0.5 ? 2 * t * t : 1 - Math.pow(-2 * t + 2, 2) / 2,

  /**
   * Cubic ease in - slower start than quadratic
   */
  easeInCubic: (t: number): number => t * t * t,

  /**
   * Cubic ease out - slower end than quadratic
   */
  easeOutCubic: (t: number): number => 1 - Math.pow(1 - t, 3),

  /**
   * Cubic ease in-out - smoother than quadratic
   */
  easeInOutCubic: (t: number): number =>
    t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2,

  /**
   * Sharp attack with quick decay - perfect for hit impacts
   */
  punch: (t: number): number => Math.exp(-t * 6) * (1 - t),

  /**
   * Bouncing decay - multiple diminishing peaks
   */
  bounce: (t: number): number => {
    const bounceFreq = 3;
    const decay = Math.exp(-t * 4);
    const bounce = Math.abs(Math.sin(t * Math.PI * bounceFreq));
    return decay * bounce;
  },

  /**
   * Explosion curve - quick rise to peak, then exponential fade
   */
  explosion: (t: number): number => {
    if (t < 0.1) {
      return t * 10;
    }
    const decayT = (t - 0.1) / 0.9;
    return Math.exp(-decayT * 4);
  },

  /**
   * Step function - no interpolation, instant snap at t >= 1
   */
  instant: (t: number): number => (t >= 1 ? 1 : 0),
};

/**
 * Resolve an easing from either a preset name or a custom function
 */
export function resolveEasing(easing: EasingFunction | EasingPreset | undefined, fallback: EasingPreset = 'linear'): EasingFunction {
  if (easing === undefined) {
    return Easing[fallback];
  }
  if (typeof easing === 'function') {
    return easing;
  }
  return Easing[easing] ?? Easing[fallback];
}

// ============================================================================
// Easing Composition Utilities
// ============================================================================

/**
 * Invert an easing function (1 - ease(t))
 */
export function invertEasing(ease: EasingFunction): EasingFunction {
  return (t: number) => 1 - ease(t);
}

/**
 * Reverse an easing function (ease(1 - t))
 */
export function reverseEasing(ease: EasingFunction): EasingFunction {
  return (t: number) => ease(1 - t);
}

/**
 * Combine two easing functions by multiplying their outputs
 */
export function multiplyEasing(a: EasingFunction, b: EasingFunction): EasingFunction {
  return (t: number) => a(t) * b(t);
}

/**
 * Create a delayed easing that stays at 0 for a portion of the duration
 * @param delay Normalized delay (0-1)
 * @param ease The easing function to apply after the delay
 */
export function delayedEasing(delay: number, ease: EasingFunction): EasingFunction {
  return (t: number): number => {
    if (t < delay) return 0;
    const adjustedT = (t - delay) / (1 - delay);
    return ease(adjustedT);
  };
}

/**
 * Create an easing from control points using linear interpolation
 * Points should be in format [[t0, v0], [t1, v1], ...] where t and v are in [0, 1]
 */
export function createPointsEasing(points: [number, number][]): EasingFunction {
  if (points.length < 2) {
    throw new Error('Points easing requires at least 2 control points');
  }

  return (t: number): number => {
    for (let i = 0; i < points.length - 1; i++) {
      const [t0, v0] = points[i];
      const [t1, v1] = points[i + 1];

      if (t >= t0 && t <= t1) {
        const localT = (t - t0) / (t1 - t0);
        return v0 + (v1 - v0) * localT;
      }
    }

    if (t <= points[0][0]) return points[0][1];
    return points[points.length - 1][1];
  };
}

/**
 * Chain multiple easings sequentially over normalized time
 * @param segments Array of [duration, easing] pairs (durations should sum to 1)
 */
export function chainEasing(segments: [number, EasingFunction][]): EasingFunction {
  const totalDuration = segments.reduce((sum, [d]) => sum + d, 0);
  
  return (t: number): number => {
    let accumulated = 0;
    for (const [duration, ease] of segments) {
      const normalizedDuration = duration / totalDuration;
      if (t <= accumulated + normalizedDuration) {
        const localT = (t - accumulated) / normalizedDuration;
        return ease(localT);
      }
      accumulated += normalizedDuration;
    }
    return 1;
  };
}

// ============================================================================
// Time Ramp Compatibility Layer (for existing TimeRamps.ts consumers)
// ============================================================================

/**
 * TimeRamp is an alias for EasingFunction for backward compatibility
 * TimeRamps typically map t→intensity where t=0 is start and t=1 is end
 */
export type TimeRamp = EasingFunction;
export type TimeRampPreset = EasingPreset;

/**
 * TimeRamps object - provides decay curves (1→0) for effects
 * Note: Standard Easing goes 0→1, TimeRamps go 1→0 for decay effects
 */
export const TimeRamps: Record<TimeRampPreset, TimeRamp> = {
  linear: (t: number): number => 1 - t,
  easeIn: (t: number): number => 1 - t * t,
  easeOut: (t: number): number => {
    const inv = 1 - t;
    return inv * inv;
  },
  easeInOut: (t: number): number => (Math.cos(t * Math.PI) + 1) / 2,
  easeInCubic: (t: number): number => 1 - t * t * t,
  easeOutCubic: (t: number): number => Math.pow(1 - t, 3),
  easeInOutCubic: (t: number): number => (Math.cos(t * Math.PI) + 1) / 2,
  punch: Easing.punch,
  bounce: Easing.bounce,
  explosion: Easing.explosion,
  instant: (t: number): number => (t >= 1 ? 0 : 1),
};

/**
 * Resolve a TimeRamp from either a preset name or a custom function
 */
export function resolveTimeRamp(ramp: TimeRamp | TimeRampPreset | undefined): TimeRamp {
  if (ramp === undefined) {
    return TimeRamps.easeOut;
  }
  if (typeof ramp === 'function') {
    return ramp;
  }
  return TimeRamps[ramp] ?? TimeRamps.easeOut;
}

/**
 * Create a bezier ramp from control points (alias for createPointsEasing)
 */
export const createBezierRamp = createPointsEasing;

/**
 * Multiply two ramps (alias for multiplyEasing)
 */
export const multiplyRamps = multiplyEasing;

/**
 * Create a delayed ramp (alias for delayedEasing)
 */
export const delayedRamp = delayedEasing;
